/**
 * A very small hyperscript helper, and a reconciler for the trees it builds.
 *
 * Screens are pure functions of state that return real DOM nodes, and every
 * change rebuilds a whole screen. What `render` does with that tree is the part
 * that matters: it walks it against the nodes already on screen and writes only
 * the differences, rather than throwing the old ones away.
 *
 * The wholesale swap it used to do was visible. Anything the browser had to
 * re-establish — an image, the caret in a field, a scroll offset, a hover — was
 * re-established on every state change, and a record whose 3D viewport reports
 * in over three separate messages therefore flickered three times as it opened.
 * Elements that survive a repaint have none of that to lose.
 *
 * Still not a virtual DOM: the new tree IS DOM, built eagerly, and the diff is
 * one pass by position with no keys. For a screen of a few hundred nodes that is
 * cheaper than the layout the old swap forced.
 */

export type Child = Node | string | number | false | null | undefined | Child[];

/** A path `d` attribute, or several to be drawn as separate paths. */
export type IconPath = string | readonly string[];

export type ClassValue = string | undefined | (string | false | null | undefined)[];
export type StyleValue = string | Partial<Record<string, string | number | undefined>> | undefined;

export interface Props {
  /** class name; falsy entries in an array are dropped */
  class?: ClassValue;
  /** inline style, either a css string or a partial style object */
  style?: StyleValue;
  /** text content shortcut */
  text?: string | number | undefined;
  /** innerHTML — only ever used with trusted, locally authored SVG markup */
  html?: string | undefined;
  /** event handlers, e.g. onclick, oninput */
  [key: string]: unknown;
}

/**
 * Handlers `h` attached, kept on the element itself.
 *
 * addEventListener offers no way to read back what is registered, and a patch has
 * to swap this render's closures for the last one's — they capture state, so a
 * stale one acts on a bridge the user has since navigated away from.
 */
const HANDLERS = Symbol('handlers');

type HandlerMap = Record<string, EventListener>;
type WithHandlers = Element & { [HANDLERS]?: HandlerMap };

function applyClass(el: Element, v: ClassValue): void {
  if (!v) return;
  const name = Array.isArray(v) ? v.filter(Boolean).join(' ') : v;
  if (name) el.setAttribute('class', name);
}

function applyStyle(el: HTMLElement | SVGElement, v: StyleValue): void {
  if (!v) return;
  if (typeof v === 'string') {
    el.setAttribute('style', v);
    return;
  }
  for (const [k, val] of Object.entries(v)) {
    if (val == null) continue;
    // Custom properties must go through setProperty.
    if (k.startsWith('--')) el.style.setProperty(k, String(val));
    else (el.style as unknown as Record<string, string>)[k] = String(val);
  }
}

export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props?: Props | null,
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);

  if (props) {
    for (const [key, value] of Object.entries(props)) {
      if (value == null || value === false) continue;

      if (key === 'class') applyClass(el, value as ClassValue);
      else if (key === 'style') applyStyle(el, value as StyleValue);
      /* A textarea has no `value` attribute — its content is its text — so
         setAttribute silently dropped it and every re-render brought the note
         back blank, however much had been typed. Set as text so the element
         still serialises for scripts/snapshot.ts, and as the property so the
         control shows it. */
      else if (key === 'value' && el.tagName === 'TEXTAREA') {
        el.textContent = String(value);
        (el as unknown as { value: string }).value = String(value);
      } else if (key === 'text') el.textContent = String(value);
      else if (key === 'html') el.innerHTML = String(value);
      else if (key.startsWith('on') && typeof value === 'function') {
        const type = key.slice(2);
        const fn = value as EventListener;
        const on = ((el as WithHandlers)[HANDLERS] ??= {});
        on[type] = fn;
        el.addEventListener(type, fn);
      } else if (value === true) el.setAttribute(key, '');
      else el.setAttribute(key, String(value));
    }
  }

  append(el, children);
  return el;
}

export function append(parent: Node, children: Child[]): void {
  for (const child of children) {
    if (child == null || child === false) continue;
    if (Array.isArray(child)) append(parent, child);
    else if (child instanceof Node) parent.appendChild(child);
    else parent.appendChild(document.createTextNode(String(child)));
  }
}

/* --------------------------- reconciling --------------------------- */

const ELEMENT = 1;

/**
 * Whether one node can be updated into the other, or has to be swapped for it.
 *
 * Namespace as well as tag: `<a>` exists in both HTML and SVG, and patching one
 * into the other would leave an element of the wrong kind in place.
 */
function interchangeable(a: Node, b: Node): boolean {
  if (a.nodeType !== b.nodeType) return false;
  if (a.nodeType !== ELEMENT) return true;
  const ea = a as Element;
  const eb = b as Element;
  return ea.tagName === eb.tagName && ea.namespaceURI === eb.namespaceURI;
}

/** Swap this render's handlers for the previous render's. */
function patchHandlers(oldEl: WithHandlers, newEl: WithHandlers): void {
  const prev = oldEl[HANDLERS];
  const next = newEl[HANDLERS];
  if (!prev && !next) return;

  for (const type of new Set([...Object.keys(prev ?? {}), ...Object.keys(next ?? {})])) {
    const before = prev?.[type];
    const after = next?.[type];
    if (before === after) continue;
    if (before) oldEl.removeEventListener(type, before);
    if (after) oldEl.addEventListener(type, after);
  }
  if (next) oldEl[HANDLERS] = next;
  else delete oldEl[HANDLERS];
}

/**
 * Carry over the live state of a form control, which is a property and not an
 * attribute — so nothing above this would have copied it.
 *
 * Assigned only when it actually differs. A control the user is typing into
 * already holds what state says it holds, and assigning a value a field already
 * has still collapses the selection and drops the caret at the end.
 */
function patchFormState(oldEl: Element, newEl: Element): void {
  const tag = oldEl.tagName;
  if (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT') return;

  const live = oldEl as HTMLInputElement;
  const wanted = newEl as HTMLInputElement;
  if (live.value !== wanted.value) live.value = wanted.value;
  if (tag === 'INPUT' && live.checked !== wanted.checked) live.checked = wanted.checked;
}

/**
 * Marks an element whose CONTENTS are not this reconciler's to manage.
 *
 * A slot, in other words: the element itself is rendered from state like anything
 * else — it can move, change class, be replaced wholesale — but whatever is inside
 * it was put there by something that owns it, and a diff against the empty element
 * the screen described would delete it.
 *
 * There is exactly one kind of thing this is for, and it is the reason it exists:
 * a live WebGL canvas. src/map/scene3d.ts and src/map/flyMap.ts each mount one into
 * a slot on the network screen after the render, because a GL context, its uploaded
 * geometry and its camera cannot be rebuilt from a tree — and the screens describe
 * those slots as empty divs, because from a screen's point of view they ARE empty.
 * Without this, the first repaint after a map finished loading took its canvas away
 * again: `patchChildren` trims what the new tree does not account for, and it
 * accounted for nothing. Both 3D views mounted, drew one frame nobody saw, and left
 * an empty box behind — no error anywhere, because nothing had gone wrong as far as
 * either side could tell.
 *
 * The modeller's two frames avoid this by living OUTSIDE the render root entirely
 * (see src/modeler/viewport.ts), which costs them having to measure and follow a
 * placeholder. This is the cheaper arrangement for something that already fills its
 * box: stay in the tree, and say so.
 *
 * ITS VALUE NAMES THE SLOT, and has to be distinct per slot rather than a bare
 * flag — see the comment in patchElement for what a shared one would do the moment
 * a user switched from one 3D view to the other.
 */
const KEEP = 'data-keep';

function patchElement(oldEl: Element, newEl: Element): void {
  /* Read BEFORE the attributes are copied across, because what matters is what
     this element was when the paint began. A diff with no keys matches by
     position, so the div holding a slot's canvas is regularly the same div that
     held something else a moment ago — the flat map's tile layer, when the user
     switches from Flat to Fly. Such an element is being REPURPOSED, not left
     alone, and its old contents have to go. Hence the value rather than the bare
     presence of the marker: two slots that are both slots are still two different
     slots, and Fly's canvas must not be inherited by Site's box. */
  const keeping =
    oldEl.getAttribute(KEEP) !== null && oldEl.getAttribute(KEEP) === newEl.getAttribute(KEEP);

  for (const attr of Array.from(newEl.attributes)) {
    if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
  }
  for (const attr of Array.from(oldEl.attributes)) {
    if (!newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
  }

  patchHandlers(oldEl, newEl);
  // Still the same slot as last paint: its own attributes and handlers are
  // patched as usual, and only what is INSIDE it is left alone. See KEEP.
  if (keeping) return;
  patchChildren(oldEl, newEl);
  // After the children: a <select>'s value means nothing until its options exist.
  patchFormState(oldEl, newEl);
}

function patchNode(oldNode: Node, newNode: Node): void {
  if (oldNode.nodeType === ELEMENT) {
    patchElement(oldNode as Element, newNode as Element);
    return;
  }
  if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
}

/**
 * Reconcile `oldParent`'s children against `newParent`'s, by position.
 *
 * No keys, so a row inserted at the top of a sorted table rewrites the text of
 * every row below it rather than moving one node. That is the trade this makes:
 * a few hundred string comparisons against the tables this app draws, in exchange
 * for a diff with nothing to get wrong about identity.
 */
function patchChildren(oldParent: Node, newParent: Node): void {
  const next = Array.from(newParent.childNodes);

  for (let i = 0; i < next.length; i++) {
    const wanted = next[i]!;
    const current = oldParent.childNodes[i];
    if (!current) oldParent.appendChild(wanted);
    else if (interchangeable(current, wanted)) patchNode(current, wanted);
    else oldParent.replaceChild(wanted, current);
  }

  while (oldParent.childNodes.length > next.length) {
    oldParent.removeChild(oldParent.lastChild as Node);
  }
}

/**
 * Make `parent`'s children match `children`, updating what is already there.
 *
 * The new tree is built first — `h` returns real elements — and then diffed into
 * place, so the nodes the user is looking at are the ones that survive: the caret
 * in a field, an image already decoded, a scroll offset, the row under the mouse.
 */
export function render(parent: Node, ...children: Child[]): void {
  const built = (parent.ownerDocument ?? document).createDocumentFragment();
  append(built, children);
  patchChildren(parent, built);
}

const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * A Lucide-style line icon. All icons in this app are single- or multi-path
 * outlines on a 24x24 grid at stroke-width 1.5, matching the design.
 */
export function icon(
  d: IconPath,
  opts: { size?: number; stroke?: string; width?: number; class?: string } = {},
): SVGSVGElement {
  const { size = 14, stroke = 'currentColor', width = 1.5 } = opts;
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('fill', 'none');
  svg.setAttribute('stroke', stroke);
  svg.setAttribute('stroke-width', String(width));
  svg.setAttribute('stroke-linecap', 'round');
  svg.setAttribute('stroke-linejoin', 'round');
  svg.setAttribute('aria-hidden', 'true');
  if (opts.class) svg.setAttribute('class', opts.class);
  for (const seg of Array.isArray(d) ? d : [d]) {
    const path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', seg);
    svg.appendChild(path);
  }
  return svg;
}

/** Icon paths used across the app. */
export const ICONS = {
  bridge: 'M2 18h20M4 18V9M20 18V9M4 12c4-5 12-5 16 0M8 18v-4M16 18v-4M12 18v-6',
  grid: 'M3 13h8V3H3zM13 21h8v-8h-8zM13 3v8h8V3zM3 21h8v-6H3z',
  camera:
    'M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2zM12 17a4 4 0 1 0 0-8 4 4 0 0 0 0 8',
  report: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M8 13h8M8 17h5',
  book: 'M4 19.5A2.5 2.5 0 0 1 6.5 17H20M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2M9 7h7',
  menu: 'M3 6h18M3 12h18M3 18h18',
  // Ruled lines against a margin — the register, which is a list of records
  // rather than a picture of a bridge.
  list: ['M9 6h12M9 12h12M9 18h12', 'M4 6h1M4 12h1M4 18h1'],
  // A folded route map — the network schematic, which places structures along
  // the road that carries them.
  map: ['M9 3 3 6v15l6-3 6 3 6-3V3l-6 3z', 'M9 3v15', 'M15 6v15'],
  // A page split into two stacked panes — the home page's register over its network.
  stacked: ['M4 3h16a1 1 0 0 1 1 1v16a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z', 'M3 11h18'],
  // Bars stepping down and to the right against a left margin — a Gantt, which
  // is what the modeller's schedule view draws a frozen baseline as.
  gantt: ['M3 4v16', 'M6 7h8', 'M9 12h9', 'M7 17h6'],
  // An axis with a rising line over it — Monitoring's charts of the site record.
  chart: ['M3 3v18h18', 'm7 15 4-5 3 3 5-7'],
  /* Arrows pushing out to the four corners, and the same arrows pulling back
     in: the Gantt's full-view pair. Not `fit`, which is the photograph's
     corner frame and means "fit what is here to the box" — these mean "change
     the box", which is the other thing. */
  expand: ['M9 3H3v6', 'M15 3h6v6', 'M21 15v6h-6', 'M3 15v6h6', 'm3 3 6 6', 'm21 3-6 6', 'm21 21-6-6', 'm3 21 6-6'],
  shrink: ['M9 3v6H3', 'M15 3v6h6', 'M15 21v-6h6', 'M9 21v-6H3', 'm3 3 6 6', 'm21 3-6 6', 'm21 21-6-6', 'm3 21 6-6'],
  refresh: ['M21 12a9 9 0 1 1-3-6.7', 'M21 3v5h-5'],
  sun: [
    'M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4',
  ],
  moon: 'M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8',
  search: ['M18 11a7 7 0 1 1-14 0 7 7 0 0 1 14 0', 'm20 20-3.5-3.5'],
  plus: 'M12 5v14M5 12h14',
  minus: 'M5 12h14',
  // The map's whole-country button: the jurisdiction, not a place in it.
  globe: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18', 'M3 12h18', 'M12 3c3 3.5 3 14.5 0 18', 'M12 3c-3 3.5-3 14.5 0 18'],
  // Four arrows out of a cross — Adjust position, which drags a structure.
  move: ['m5 9-3 3 3 3', 'm9 5 3-3 3 3', 'm15 19-3 3-3-3', 'm19 9 3 3-3 3', 'M2 12h20', 'M12 2v20'],
  x:'M6 6l12 12M18 6L6 18',
  /* The enlarged photograph's zoom toolbar — see src/components/photoZoom.ts.
     `pixels` is a frame with one pixel drawn in it: actual size, 1:1. */
  zoomIn: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16', 'm21 21-4.3-4.3', 'M11 8v6', 'M8 11h6'],
  zoomOut: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16', 'm21 21-4.3-4.3', 'M8 11h6'],
  fit: ['M3 9V5a2 2 0 0 1 2-2h4', 'M15 3h4a2 2 0 0 1 2 2v4', 'M21 15v4a2 2 0 0 1-2 2h-4', 'M9 21H5a2 2 0 0 1-2-2v-4'],
  pixels: ['M4 4h16v16H4z', 'M10.5 10.5h3v3h-3z'],
  chevronDown: 'm6 9 6 6 6-6',
  arrowLeft: ['M19 12H5', 'm12 19-7-7 7-7'],
  cube: ['m12 2 9 5v10l-9 5-9-5V7z', 'M12 22V12', 'm21 7-9 5-9-5'],
  /* The construction played on the model: the cube, with a play badge. */
  simulate: ['M19 10.5V7l-8-4.5L3 7v9l8 4.5', 'M3 7l8 4.5L19 7', 'M11 11.5v9', 'M15 14v8l7-4z'],
  /* The Defect Inspector's canvas toolbar — see src/inspector/tools.ts.

     `ortho` is a box whose edges stay parallel, against `cube`'s converging ones:
     the pair is the projection switch, and the difference between the two icons is
     precisely the difference between the two projections. Drawn as a front face
     with a second face offset from it, rather than as a perspective frustum. */
  ortho: ['M3 8h13v13H3z', 'M8 3h13v13', 'M3 8l5-5M16 8l5-5M16 21l5-5'],
  /* A pointer arrow. The toolbar's select-components toggle, which changes what a
     tap on the model MEANS — so the icon is the tap itself rather than a picture of
     a bridge part, and it reads against the crosshair the canvas otherwise wears. */
  pointer: ['M4 4l7.07 17 2.51-7.39L21 11.07z'],
  /* Concentric rings with an axis crossing them: the point the camera turns
     about, which is what the button sets. The modeller draws the same figure. */
  orbitPoint: [
    'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18',
    'M12 14.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5',
    'M12 3v2.5M12 18.5V21M3 12h2.5M18.5 12H21',
  ],
  // A magnifier over a facet — the modeller's defect inspector, which is a lens on
  // the geometry rather than a list.
  inspect: ['m3 7 7-4 7 4v6', 'M3 7v6l7 4', 'M10 11 3 7m7 4 7-4', 'M15 17a3 3 0 1 0 0-6 3 3 0 0 0 0 6', 'm20 22-2.5-2.5'],
  viewTable: ['M3 4h18v16H3z', 'M3 9h18M3 14h18'],
  viewSplit: ['M3 4h18v16H3z', 'M14 4v16M3 9h11'],
  settings: [
    'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6',
    'M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-2.9 1.2 2 2 0 1 1-4 0 1.7 1.7 0 0 0-2.9-1.2l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1A1.7 1.7 0 0 0 4.6 15a2 2 0 1 1 0-4 1.7 1.7 0 0 0 1.2-2.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1A1.7 1.7 0 0 0 11.5 4.6a2 2 0 1 1 4 0 1.7 1.7 0 0 0 2.9 1.2l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1A1.7 1.7 0 0 0 22 11.5a2 2 0 1 1 0 4z',
  ],
  image: ['M3 3h18v18H3z', 'm3 16 5-5 4 4 3-3 6 6', 'M8.5 9.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3'],
  print: ['M6 9V2h12v7', 'M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2', 'M6 14h12v8H6z'],
  edit: ['M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z', 'M15 5l4 4'],
  // A file coming down into a tray — bringing a model in from CSiBridge, as
  // against `cube`, which stands for the model once it is here.
  import: ['M12 3v12', 'm8 11 4 4 4-4', 'M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3'],
  /* A phone with an arrow coming down into it — putting THIS APPLICATION on the
     device, which is a different act from `import`'s bringing a model in. The
     arrow is inside the frame rather than pointing at it: what arrives is the app
     itself, and where it arrives is that device. */
  install: [
    'M7 2h10a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2',
    'M12 7v7',
    'm9 11 3 3 3-3',
  ],
  trash: ['M3 6h18', 'M8 6V4h8v2', 'M6 6v14a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V6', 'M10 11v6M14 11v6'],
  /* A balance — Bridge Rating, whose question is what a structure may be asked
     to carry. A beam on a post with a pan at each end, because the rating IS a
     comparison: capacity on one side, the load on the other. */
  scale: ['M12 4v16', 'M8 20h8', 'M4 8h16', 'm4 8-2.5 5h5z', 'm20 8-2.5 5h5z'],
  /* A hard hat — Construction Monitoring, which is the record of a site while
     it is being built. A site, not a document: the other construction icon is
     the Gantt, and a second chart beside it would not say which was which. */
  hardhat: ['M3 18h18', 'M5 18v-3a7 7 0 0 1 14 0v3', 'M10 8.4V5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v3.4'],
  /* Two figures rather than one, because this marks the screen that lists other
     people — a single silhouette reads as "your profile", which is the other
     thing and is on Options. */
  /* The ribbon's own vocabulary (src/components/ribbon.ts). */
  alert: ['M12 3 2 20h20z', 'M12 10v4', 'M12 17h.01'],
  clipboard: ['M9 3h6v3H9z', 'M8 4.5H5V21h14V4.5h-3', 'M8.5 12h7', 'M8.5 16h4'],
  calendar: ['M3.5 5h17v15h-17z', 'M3.5 9.5h17', 'M8 3v4M16 3v4'],
  incident: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18', 'M12 7.5v5.5', 'M12 16.5h.01'],
  /* The incident form's kinds (src/screens/siteDialogs.ts), besides the near
     miss, which is `incident` itself. */
  firstAid: ['M3 7.5h18v13H3z', 'M9 7.5v-3h6v3', 'M12 11v6M9 14h6'],
  lostTime: ['M3.5 5h17v15h-17z', 'M3.5 9.5h17', 'M8 3v4M16 3v4', 'm9.5 12.5 5 5M14.5 12.5l-5 5'],
  dangerous: ['M8.3 2.5h7.4l5.8 5.8v7.4l-5.8 5.8H8.3l-5.8-5.8V8.3z', 'M12 7.5v5.5', 'M12 16.5h.01'],
  environmental: ['M12 2.5c3.5 4.5 6 7.8 6 10.9a6 6 0 0 1-12 0c0-3.1 2.5-6.4 6-10.9z', 'M2.5 21.5c2-1.3 4-1.3 6 0s4 1.3 6 0 4-1.3 7 0'],
  folder: 'M3 6.5A1.5 1.5 0 0 1 4.5 5H9l2 2.5h8.5A1.5 1.5 0 0 1 21 9v9.5a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 18.5z',
  panel: ['M3 4h18v16H3z', 'M9 4v16'],
  /* The ribbon's two layouts, for its layout menu: a band over large buttons,
     and one row of small ones. */
  ribbon: ['M3 4h18v16H3z', 'M3 8h18', 'M6 11h4v5H6z', 'M12 11h4v5h-4z', 'M18 11.5h1M18 14h1'],
  ribbonLine: ['M3 8.5h18v7H3z', 'M6 12h2M10 12h2M14.5 12h4'],
  /* Visual Studio's Auto Hide pushpin, upright — pinned open. */
  pin: ['M12 17v5', 'M9 3h6', 'M10 3v6.5L7 13v2h10v-2l-3-3.5V3'],
  arrowUp: 'm6 15 6-6 6 6',
  chevronRight: 'm9 6 6 6-6 6',
  chevronLeft: 'm15 6-6 6 6 6',
  help: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18', 'M9.2 9.2a2.9 2.9 0 1 1 4.3 2.6c-.9.5-1.5 1.1-1.5 2.2', 'M12 17h.01'],
  language: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18', 'M3 12h18', 'M12 3c2.5 2.6 3.7 5.6 3.7 9s-1.2 6.4-3.7 9c-2.5-2.6-3.7-5.6-3.7-9S9.5 5.6 12 3'],
  people: [
    'M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2',
    'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8',
    'M22 21v-2a4 4 0 0 0-3-3.87',
    'M16 3.13a4 4 0 0 1 0 7.75',
  ],
  /* The account menu's. */
  user: ['M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8', 'M4.5 20.5c.8-3.9 3.8-6 7.5-6s6.7 2.1 7.5 6'],
  key: ['M7.5 20a4 4 0 1 0 0-8 4 4 0 0 0 0 8', 'M10.3 13.2 19.5 4', 'M16.5 7l2.2 2.2', 'M14.2 9.3l1.8 1.8'],
  logout: ['M9 4H5v16h4', 'M15 8l4 4-4 4', 'M19 12H9'],
  check: 'm5 12.5 4.5 4.5 9.5-10',
  /* The sign-in page's show / hide password. */
  eye: ['M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z', 'M9 12a3 3 0 1 0 6 0 3 3 0 1 0-6 0'],
  eyeOff: [
    'M3 3l18 18',
    'M10.6 5.1A10.9 10.9 0 0 1 12 5c6.5 0 10 7 10 7a17.6 17.6 0 0 1-3.2 4.2',
    'M6.6 6.6C3.8 8.4 2 12 2 12s3.5 7 10 7a10 10 0 0 0 5.4-1.6',
    'M9.9 9.9a3 3 0 0 0 4.2 4.2',
  ],
  monitor: ['M3 4.5h18v12.5H3z', 'M9 21h6', 'M12 17v4'],
  phone: ['M7 2.5h10v19H7z', 'M11 18.5h2'],
} as const satisfies Record<string, IconPath>;

/** Debounce helper used by the register's search field. */
export function debounce<A extends unknown[]>(fn: (...args: A) => void, ms: number): (...args: A) => void {
  let t: number | undefined;
  return (...args: A) => {
    if (t !== undefined) window.clearTimeout(t);
    t = window.setTimeout(() => fn(...args), ms);
  };
}
